import { useState, useEffect } from 'react';
import { X, Shield, BarChart3, Megaphone, ChevronDown } from 'lucide-react';

const CONSENT_KEY = 'cookie_consent';
const CONSENT_TIME_KEY = 'cookie_consent_time';

export function CookieConsent({ content, theme, forceOpen, onClose }) {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  const config = content?.cookie_consent || {};
  const enabled = config.enabled !== false;
  const position = config.position || 'bottom';
  const resetDays = parseInt(config.reset_days) || 90;
  const heading = config.heading || 'We value your privacy';
  const description = config.description || 'We use cookies to enhance your browsing experience and analyse our traffic. Choose your preferences below.';

  // Auto-show on first visit or after reset period
  useEffect(() => {
    if (!enabled) return;
    const consentTime = localStorage.getItem(CONSENT_TIME_KEY);
    if (consentTime) {
      const elapsed = Date.now() - parseInt(consentTime);
      const resetMs = resetDays * 24 * 60 * 60 * 1000;
      if (elapsed < resetMs) return;
    }
    const timer = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(timer);
  }, [enabled, resetDays]);

  // Handle forceOpen from parent (cookie settings button)
  useEffect(() => {
    if (forceOpen) {
      // Load saved preferences when reopening
      try {
        const saved = JSON.parse(localStorage.getItem(CONSENT_KEY));
        if (saved) setPreferences(saved);
      } catch {}
      setShowPreferences(true);
      setVisible(true);
    }
  }, [forceOpen]);

  const close = () => {
    setVisible(false);
    setShowPreferences(false);
    if (onClose) onClose();
  };

  const saveConsent = (prefs) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
    localStorage.setItem(CONSENT_TIME_KEY, String(Date.now()));
    close();
  };

  const handleAcceptAll = () => saveConsent({ essential: true, analytics: true, marketing: true });
  const handleDecline = () => saveConsent({ essential: true, analytics: false, marketing: false });
  const handleSavePreferences = () => saveConsent(preferences);

  if (!visible) return null;

  const isDark = theme === 'dark';
  const posClass = position === 'top' ? 'top-0' : 'bottom-0';

  return (
    <>
      <div className="fixed inset-0 z-[99] bg-black/20 backdrop-blur-[1px]" onClick={close} />
      <div
        className={`fixed left-0 right-0 ${posClass} z-[100] px-4 py-4 sm:px-6`}
        data-testid="cookie-consent-banner"
      >
        <div
          className={`max-w-4xl mx-auto rounded-2xl border shadow-2xl ${
            isDark
              ? 'bg-gray-900/95 border-gray-700 backdrop-blur-md'
              : 'bg-white/95 border-gray-200 backdrop-blur-md'
          }`}
        >
          <div className="p-5 sm:p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-2.5">
                <div className={`p-1.5 rounded-lg ${isDark ? 'bg-[#007a33]/20' : 'bg-[#007a33]/10'}`}>
                  <Shield className="w-4 h-4 text-[#007a33]" />
                </div>
                <h3 className={`font-semibold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {heading}
                </h3>
              </div>
              <button
                onClick={close}
                className={`p-1 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                data-testid="cookie-close-btn"
                aria-label="Close cookie notice"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Description */}
            <p className={`text-sm leading-relaxed mb-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {description}{' '}
              <a
                href="/privacy-policy"
                className="text-[#007a33] hover:underline font-medium"
                data-testid="cookie-privacy-link"
              >
                Privacy Policy
              </a>
            </p>

            {/* Preferences Panel */}
            {showPreferences && (
              <div className={`mb-5 rounded-xl border p-4 space-y-3 ${isDark ? 'bg-gray-800/60 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                {[
                  { key: 'essential', label: 'Essential', desc: 'Required for the site to function', icon: Shield, locked: true },
                  { key: 'analytics', label: 'Analytics', desc: 'Help us understand how visitors use our site', icon: BarChart3, locked: false },
                  { key: 'marketing', label: 'Marketing', desc: 'Used to deliver relevant advertisements', icon: Megaphone, locked: false },
                ].map((cat) => (
                  <div key={cat.key} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <cat.icon className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      <div className="min-w-0">
                        <span className={`text-sm font-medium block ${isDark ? 'text-white' : 'text-gray-900'}`}>{cat.label}</span>
                        <span className={`text-xs block ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{cat.desc}</span>
                      </div>
                    </div>
                    <div
                      onClick={() => !cat.locked && setPreferences(p => ({ ...p, [cat.key]: !p[cat.key] }))}
                      role="switch"
                      className={`relative w-12 h-6 rounded-full flex-shrink-0 overflow-hidden transition-colors ${
                        cat.locked || preferences[cat.key]
                          ? 'bg-[#007a33]'
                          : isDark ? 'bg-gray-600' : 'bg-gray-300'
                      } ${cat.locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                      data-testid={`cookie-toggle-${cat.key}`}
                    >
                      <span className={`absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                        cat.locked || preferences[cat.key] ? 'translate-x-6' : 'translate-x-0'
                      }`} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-5 py-3 rounded-xl text-sm font-semibold bg-[#007a33] hover:bg-[#005c26] text-white transition-colors"
                data-testid="cookie-accept-all-btn"
              >
                Accept All
              </button>
              <button
                onClick={() => showPreferences ? handleSavePreferences() : setShowPreferences(true)}
                className={`flex-1 px-5 py-3 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
                data-testid="cookie-manage-btn"
              >
                {showPreferences ? 'Save Preferences' : 'Manage'}
                {!showPreferences && <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={handleDecline}
                className={`flex-1 px-5 py-3 rounded-xl text-sm font-semibold transition-colors ${
                  isDark
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="cookie-decline-btn"
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/** Returns true if user has already set cookie preferences */
export function hasCookieConsent() {
  return !!localStorage.getItem(CONSENT_TIME_KEY);
}
